// ─────────────────────────────────────────────────────────────────────────────
// Settings → Workflow Configuration → ACT (ALPACA Custom Trigger)
//
// STATIC SAMPLE DATA, ported verbatim from the approved prototype
// (ALPACA_CAT_Standardized_ID_Prototype.html, 2026-08-13).
//
// Nothing here is persisted yet — this is deliberate. The screen exists so the
// LAYOUT and the vocabulary can be reviewed before any table is designed, and
// the fastest way to get that wrong is to invent a schema first and discover at
// review that the tabs should be shaped differently.
//
// When persistence lands, everything below is replaced by an API call and this
// file goes away. It is kept separate from the page component precisely so that
// swap is a one-import change rather than surgery on the JSX — and so the shape
// of the eventual DTOs is already visible here.
//
// ⚠️ A ACT ID ("ACT-001") is a human-readable SEQUENTIAL id. When this is wired
// to a database it MUST be minted through ISequentialIdGenerator, never
// MAX(id)+1 — CLAUDE.md §14a Rule B. The ids below are fixed sample values, so
// the "Add Method" action here only previews the next number; it does not
// reserve one.
// ─────────────────────────────────────────────────────────────────────────────

/** One rule row in the ACT Rules grid. */
export interface ActRule {
  priority: number;
  ruleId:   string;
  name:     string;
  trigger:  string;
  condition: string;
  action:   string;
  target:   string;
  status:   'Active' | 'Disabled';
}

/** A prep flow available to a ACT configuration. */
export interface PrepFlow {
  name:      string;
  purpose:   string;
  isDefault: boolean;
}

/** Everything the six tabs need for one ACT configuration. */
export interface ActConfig {
  key:      string;
  catId:    string;
  test:     string;
  method:   string;
  matrix:   string;
  process:  string;
  /** Short "why this exists" line shown under the method in the left panel. */
  summary:  string;
  status:   'Active' | 'Disabled';

  // Trigger Setup
  triggerObject:   string;
  primaryTrigger:  string;
  reevaluateOn:    string;
  triggerField:    string;
  executionTiming: string;
  runOnce:         string;
  stopFurtherRules: string;

  // Condition Builder
  whenObject:      string;
  whenEvent:       string;
  conditionField:  string;
  operator:        string;
  value:           string;
  /** Optional AND leg — only Cyanide has one in the sample data. */
  secondary?:      { field: string; operator: string; value: string; flow: string };
  thenAction:      string;
  thenTarget:      string;

  // Prep Flow Setup
  flowType:        string;
  conditionSource: string;
  matchedFlow:     string;
  elseFlow:        string;
  flowResult:      string;
  reevaluateOnChange: boolean;
  prepFlows:       PrepFlow[];

  // Supporting Actions.
  //
  // 2026-08-14: these are no longer EDITED anywhere — the Supporting Actions tab
  // restated the Condition Builder's THEN, and none of these values ever reached
  // a rule, the logic preview, or a validation check. They are kept at their
  // COMMON defaults rather than deleted because the eventual schema may still
  // want them (an ACT action that is not "pick a prep flow" needs a target and a
  // value). If the schema lands without them, delete them here and in COMMON.
  actionCategory:  string;
  action:          string;
  targetObject:    string;
  targetField:     string;
  valueSource:     string;
  actionValue:     string;
  elseAction:      string;
  allowManualOverride: boolean;

  rules: ActRule[];
}

/** Dropdown vocabularies. One source of truth so a tab and its preview text can
 *  never drift apart. */
export const ACT_OPTIONS = {
  triggerObject:   ['Sample Test', 'Sample', 'Aliquot', 'Prep Batch Row'],
  primaryTrigger:  ['On Prep Flow Evaluation', 'When Test Enters Sample Prep', 'On Record Create', 'On Status Change'],
  reevaluateOn:    ['Condition Field Changed', 'Never', 'Any Row Change', 'Manual Only'],
  conditionField:  ['NTU', 'Residual Chlorine', 'Chloride', 'Sulfide', 'Oxidizer', 'Emulsion', 'Material Type', 'pH'],
  executionTiming: ['Immediate', 'Before Save', 'After Save', 'Before Complete'],
  runOnce:         ['No – Re-evaluate as needed', 'Yes – First match only'],
  yesNo:           ['No', 'Yes'],
  whenEvent:       ['Loaded into Prep Batch', 'Field Changed', 'Status Changed'],
  operator:        ['>', '≤', '=', '≠'],
  thenAction:      ['Select Prep Flow', 'Set Field Value', 'Create Aliquot', 'Create Prep Task'],
  thenTarget:      ['Sample Test', 'Prep Batch Row', 'Aliquot'],
  flowType:        ['Sample Preparation', 'Pretreatment', 'Extraction', 'Matrix Reduction'],
  conditionSource: ['Measured Result', 'Sample Observation', 'Sample Property', 'Previous Test Result'],
  flowResult:      ['Route sample/test to selected prep', 'Create required prep step', 'Assign prep method and route'],
  actionCategory:  ['Prep Flow', 'Routing', 'Data', 'Aliquot', 'Validation'],
  targetObject:    ['Sample Test', 'Prep Batch Row', 'Sample', 'Aliquot'],
  targetField:     ['PrepFlow', 'PrepMethod', 'FunctionType', 'DatePrepared'],
  valueSource:     ['Fixed Value', 'Method Default', 'Source Field', 'Calculated Value'],
  elseAction:      ['Set SamplePrep = No', 'Do Nothing', 'Hold for Review'],
  status:          ['Active', 'Disabled'],
} as const;

/** Actions offered per category — the prototype's `loadActions()` behaviour.
 *  No longer rendered: the Supporting Actions tab that used it was removed on
 *  2026-08-14 as a duplicate of the THEN. Kept alongside the model fields above
 *  for the same reason. */
export const ACTIONS_BY_CATEGORY: Record<string, string[]> = {
  'Prep Flow':  ['Select Prep Flow', 'Assign Prep Method', 'Create Prep Step', 'Add to Prep Batch'],
  'Routing':    ['Route to Department', 'Assign Analyst', 'Hold for Review'],
  'Data':       ['Set Field Value', 'Copy Field Value', 'Clear Field'],
  'Aliquot':    ['Create Aliquot', 'Split Sample', 'Assign Container'],
  'Validation': ['Flag for Review', 'Block Completion', 'Require Comment'],
};

/** Matrix → Test → Method. The "Add Method" dialog cascades through this, so a
 *  Test can only be picked once its Matrix is chosen and a Method once its Test
 *  is — the rule the prototype's note calls out explicitly. */
export const MATRIX_TESTS: Record<string, { test: string; methods: string[] }[]> = {
  'Water': [
    { test: 'Metals_ICP_MS',    methods: ['EPA 200.8'] },
    { test: 'Metals_ICP_OES',   methods: ['EPA 200.7'] },
    { test: 'BOD',              methods: ['SM 5210 B'] },
    { test: 'Cyanide',          methods: ['SM 4500-CN', 'EPA 335.4'] },
    { test: 'Oil & Grease',     methods: ['EPA 1664A', 'EPA 1664B'] },
    { test: 'TOC / DOC',        methods: ['SM 5310 B'] },
    { test: 'Total Phosphorus', methods: ['EPA 365.1'] },
  ],
  'Wastewater': [
    { test: 'BOD',              methods: ['SM 5210 B'] },
    { test: 'COD',              methods: ['SM 5220 D', 'EPA 410.4'] },
    { test: 'Cyanide',          methods: ['SM 4500-CN', 'EPA 335.4'] },
    { test: 'Oil & Grease',     methods: ['EPA 1664A', 'EPA 1664B'] },
    { test: 'TOC / DOC',        methods: ['SM 5310 B'] },
    { test: 'Total Phosphorus', methods: ['EPA 365.1'] },
  ],
  'Water / Wastewater': [
    { test: 'Metals_ICP_OES',   methods: ['EPA 200.7'] },
    { test: 'BOD',              methods: ['SM 5210 B'] },
    { test: 'COD',              methods: ['SM 5220 D', 'EPA 410.4'] },
    { test: 'Cyanide',          methods: ['SM 4500-CN', 'EPA 335.4'] },
    { test: 'Oil & Grease',     methods: ['EPA 1664A', 'EPA 1664B'] },
  ],
  'Bulk Material': [
    { test: 'Bulk Asbestos PLM', methods: ['EPA 600/R-93/116'] },
  ],
};

export const PROCESS_OPTIONS = ['Sample Prep'];

/** Defaults shared by every sample configuration, kept in one place so the
 *  seven entries below only state what actually differs between them. */
const COMMON = {
  process:  'Sample Prep',
  status:   'Active' as const,
  triggerObject:   'Sample Test',
  primaryTrigger:  'On Prep Flow Evaluation',
  reevaluateOn:    'Condition Field Changed',
  executionTiming: 'Immediate',
  runOnce:         'No – Re-evaluate as needed',
  stopFurtherRules: 'No',
  whenObject:      'Sample Test',
  whenEvent:       'Loaded into Prep Batch',
  thenAction:      'Select Prep Flow',
  thenTarget:      'Sample Test',
  flowResult:      'Route sample/test to selected prep',
  reevaluateOnChange: true,
  actionCategory:  'Prep Flow',
  action:          'Select Prep Flow',
  targetObject:    'Sample Test',
  targetField:     'PrepFlow',
  valueSource:     'Fixed Value',
  actionValue:     'Yes',
  elseAction:      'Set SamplePrep = No',
  allowManualOverride: true,
};

export const ACT_CONFIGS: ActConfig[] = [
  {
    ...COMMON,
    key: 'metals', catId: 'ACT-001', test: 'Metals_ICP_MS', method: 'EPA 200.8',
    matrix: 'Water', summary: 'NTU selects prep flow',
    triggerField: 'NTU', conditionField: 'NTU', operator: '>', value: '1.0',
    flowType: 'Sample Preparation', conditionSource: 'Measured Result',
    matchedFlow: 'Filtration / Prep Required', elseFlow: 'Direct Metals Prep',
    prepFlows: [
      { name: 'Filtration / Prep Required', purpose: 'Route sample/test through filtration-related preparation', isDefault: false },
      { name: 'Direct Metals Prep',         purpose: 'Use normal metals preparation path',                       isDefault: true  },
    ],
    rules: [
      { priority: 10, ruleId: 'R01', name: 'Select Filtration Prep', trigger: 'On Prep Evaluation + NTU Changed', condition: 'NTU > 1.0',  action: 'Select Prep Flow: Filtration / Prep Required', target: 'Sample Test', status: 'Active' },
      { priority: 20, ruleId: 'R02', name: 'Select Direct Prep',     trigger: 'On Prep Evaluation + NTU Changed', condition: 'NTU ≤ 1.0',  action: 'Select Prep Flow: Direct Metals Prep',         target: 'Sample Test', status: 'Active' },
    ],
  },
  {
    ...COMMON,
    key: 'metals2007', catId: 'ACT-002', test: 'Metals_ICP_OES', method: 'EPA 200.7',
    matrix: 'Water', summary: 'NTU selects prep flow',
    triggerField: 'NTU', conditionField: 'NTU', operator: '>', value: '1.0',
    flowType: 'Sample Preparation', conditionSource: 'Measured Result',
    matchedFlow: 'Filtration / EPA 200.7 Prep Required', elseFlow: 'Direct EPA 200.7 Metals Prep',
    prepFlows: [
      { name: 'Filtration / EPA 200.7 Prep Required', purpose: 'Route the sample/test through the configured filtration/preparation path for EPA 200.7', isDefault: false },
      { name: 'Direct EPA 200.7 Metals Prep',         purpose: 'Use the default EPA 200.7 metals preparation path',                                     isDefault: true  },
    ],
    rules: [
      { priority: 10, ruleId: 'R01', name: 'Select EPA 200.7 Filtration Prep', trigger: 'On Prep Evaluation + NTU Changed', condition: 'NTU > 1.0', action: 'Select Prep Flow: Filtration / EPA 200.7 Prep Required', target: 'Sample Test', status: 'Active' },
      { priority: 20, ruleId: 'R02', name: 'Select EPA 200.7 Direct Prep',     trigger: 'On Prep Evaluation + NTU Changed', condition: 'NTU ≤ 1.0', action: 'Select Prep Flow: Direct EPA 200.7 Metals Prep',         target: 'Sample Test', status: 'Active' },
    ],
  },
  {
    ...COMMON,
    key: 'bod', catId: 'ACT-003', test: 'BOD', method: 'SM 5210 B',
    matrix: 'Water / Wastewater', summary: 'chlorine selects prep flow',
    triggerField: 'Residual Chlorine', conditionField: 'Residual Chlorine', operator: '=', value: 'Present',
    flowType: 'Pretreatment', conditionSource: 'Measured Result',
    matchedFlow: 'Dechlorination Prep', elseFlow: 'Normal BOD Prep',
    prepFlows: [
      { name: 'Dechlorination Prep', purpose: 'Neutralize residual chlorine before BOD setup', isDefault: false },
      { name: 'Normal BOD Prep',     purpose: 'Proceed directly to normal BOD preparation',    isDefault: true  },
    ],
    rules: [
      { priority: 10, ruleId: 'R01', name: 'Require Dechlorination', trigger: 'On Prep Evaluation + Chlorine Changed', condition: 'Residual Chlorine = Present',     action: 'Select Prep Flow: Dechlorination Prep', target: 'Sample Test', status: 'Active' },
      { priority: 20, ruleId: 'R02', name: 'Normal BOD Prep',        trigger: 'On Prep Evaluation + Chlorine Changed', condition: 'Residual Chlorine = Not Present', action: 'Select Prep Flow: Normal BOD Prep',     target: 'Sample Test', status: 'Active' },
    ],
  },
  {
    ...COMMON,
    key: 'cod', catId: 'ACT-004', test: 'COD', method: 'SM 5220 / EPA 410.4',
    matrix: 'Wastewater', summary: 'chloride selects prep flow',
    triggerField: 'Chloride', conditionField: 'Chloride', operator: '>', value: 'Method Limit',
    flowType: 'Pretreatment', conditionSource: 'Measured Result',
    matchedFlow: 'High-Chloride Pretreatment', elseFlow: 'Standard COD Digestion',
    prepFlows: [
      { name: 'High-Chloride Pretreatment', purpose: 'Apply configured high-chloride interference preparation', isDefault: false },
      { name: 'Standard COD Digestion',     purpose: 'Use standard COD digestion preparation',                 isDefault: true  },
    ],
    rules: [
      { priority: 10, ruleId: 'R01', name: 'High Chloride Route', trigger: 'On Prep Evaluation + Chloride Changed', condition: 'Chloride > Method Limit', action: 'Select Prep Flow: High-Chloride Pretreatment', target: 'Sample Test', status: 'Active' },
      { priority: 20, ruleId: 'R02', name: 'Standard COD Route',  trigger: 'On Prep Evaluation + Chloride Changed', condition: 'Chloride ≤ Method Limit', action: 'Select Prep Flow: Standard COD Digestion',     target: 'Sample Test', status: 'Active' },
    ],
  },
  {
    ...COMMON,
    key: 'cyanide', catId: 'ACT-005', test: 'Cyanide', method: 'Cyanide Method',
    matrix: 'Water / Wastewater', summary: 'interference selects prep flow',
    triggerField: 'Sulfide', conditionField: 'Sulfide', operator: '=', value: 'Present',
    // The only sample configuration with a second condition leg — it is what
    // proves the AND row in the Condition Builder actually renders.
    secondary: { field: 'Oxidizer', operator: '=', value: 'Present', flow: 'Oxidizer Neutralization' },
    flowType: 'Pretreatment', conditionSource: 'Sample Observation',
    matchedFlow: 'Sulfide Pretreatment', elseFlow: 'Normal Cyanide Prep',
    prepFlows: [
      { name: 'Sulfide Pretreatment',    purpose: 'Apply sulfide interference pretreatment before cyanide prep',  isDefault: false },
      { name: 'Oxidizer Neutralization', purpose: 'Neutralize oxidizer interference before cyanide prep',         isDefault: false },
      { name: 'Normal Cyanide Prep',     purpose: 'Use normal cyanide preparation if no interference is present', isDefault: true  },
    ],
    rules: [
      { priority: 10, ruleId: 'R01', name: 'Sulfide Pretreatment',    trigger: 'On Prep Evaluation + Sulfide Changed',  condition: 'Sulfide = Present',               action: 'Select Prep Flow: Sulfide Pretreatment',    target: 'Sample Test', status: 'Active' },
      { priority: 20, ruleId: 'R02', name: 'Oxidizer Neutralization', trigger: 'On Prep Evaluation + Oxidizer Changed', condition: 'Oxidizer = Present',              action: 'Select Prep Flow: Oxidizer Neutralization', target: 'Sample Test', status: 'Active' },
      { priority: 30, ruleId: 'R03', name: 'Normal Cyanide Prep',     trigger: 'On Prep Evaluation',                    condition: 'No interference condition matched', action: 'Select Prep Flow: Normal Cyanide Prep',    target: 'Sample Test', status: 'Active' },
    ],
  },
  {
    ...COMMON,
    key: 'oil', catId: 'ACT-006', test: 'Oil & Grease', method: 'EPA 1664',
    matrix: 'Water / Wastewater', summary: 'emulsion selects prep flow',
    triggerField: 'Emulsion', conditionField: 'Emulsion', operator: '=', value: 'Yes',
    flowType: 'Extraction', conditionSource: 'Sample Observation',
    matchedFlow: 'Emulsion-Breaking Prep', elseFlow: 'Normal Extraction',
    prepFlows: [
      { name: 'Emulsion-Breaking Prep', purpose: 'Route through emulsion-breaking preparation before extraction', isDefault: false },
      { name: 'Normal Extraction',      purpose: 'Proceed with normal extraction',                                isDefault: true  },
    ],
    rules: [
      { priority: 10, ruleId: 'R01', name: 'Emulsion Route',    trigger: 'On Prep Evaluation + Emulsion Changed', condition: 'Emulsion = Yes', action: 'Select Prep Flow: Emulsion-Breaking Prep', target: 'Sample Test', status: 'Active' },
      { priority: 20, ruleId: 'R02', name: 'Normal Extraction', trigger: 'On Prep Evaluation + Emulsion Changed', condition: 'Emulsion = No',  action: 'Select Prep Flow: Normal Extraction',      target: 'Sample Test', status: 'Active' },
    ],
  },
  {
    ...COMMON,
    key: 'plm', catId: 'ACT-007', test: 'Bulk Asbestos PLM', method: 'PLM Method',
    matrix: 'Bulk Material', summary: 'material type selects prep flow',
    triggerField: 'Material Type', conditionField: 'Material Type', operator: '=', value: 'Bound Matrix',
    flowType: 'Matrix Reduction', conditionSource: 'Sample Property',
    matchedFlow: 'Matrix Reduction Prep', elseFlow: 'Direct PLM Prep',
    prepFlows: [
      { name: 'Matrix Reduction Prep', purpose: 'Use additional matrix-reduction preparation', isDefault: false },
      { name: 'Direct PLM Prep',       purpose: 'Proceed with direct PLM preparation',         isDefault: true  },
    ],
    rules: [
      { priority: 10, ruleId: 'R01', name: 'Bound Matrix Route', trigger: 'On Prep Evaluation + Material Type Changed', condition: 'Material Type = Bound Matrix', action: 'Select Prep Flow: Matrix Reduction Prep', target: 'Sample Test', status: 'Active' },
      { priority: 20, ruleId: 'R02', name: 'Direct PLM Route',   trigger: 'On Prep Evaluation + Material Type Changed', condition: 'Material Type ≠ Bound Matrix', action: 'Select Prep Flow: Direct PLM Prep',       target: 'Sample Test', status: 'Active' },
    ],
  },
];

/** The plain-English restatement under the Condition Builder.
 *
 *  Generated rather than stored: it is the ONE place a reviewer can check that
 *  the tabs say what they think they say, so it has to be derived from the live
 *  values or it would drift the moment someone edits a field. */
export function buildLogicPreview(c: ActConfig): string {
  const lines = [
    `WHEN ${c.whenObject} ${c.whenEvent.toLowerCase()}`,
    `IF ${c.conditionField} ${c.operator} ${c.value}`,
  ];
  if (c.secondary) {
    lines.push(`AND ${c.secondary.field} ${c.secondary.operator} ${c.secondary.value}`);
  }
  lines.push(`THEN ${c.thenAction} = "${c.matchedFlow}"`);
  lines.push(`ELSE ${c.thenAction} = "${c.elseFlow}"`);
  lines.push(`RE-EVALUATE WHEN ${c.triggerField} changes`);
  return lines.join('\n');
}

/** The Validation / Audit tab. Derived from the configuration rather than
 *  hard-coded so an incomplete setup actually reports a failure — a checklist
 *  that always says "Pass" teaches people to stop reading it. */
export interface ValidationCheck { check: string; pass: boolean; message: string }

export function validateConfig(c: ActConfig): ValidationCheck[] {
  const priorities = c.rules.map(r => r.priority);
  const duplicatePriority = priorities.length !== new Set(priorities).size;
  const flowNames = c.prepFlows.map(f => f.name);

  return [
    {
      check: 'Trigger defined',
      pass: !!c.primaryTrigger && !!c.triggerField,
      message: c.primaryTrigger && c.triggerField
        ? `${c.primaryTrigger} with re-evaluation on ${c.triggerField}`
        : 'A primary trigger and a condition field are both required',
    },
    {
      check: 'Condition valid',
      pass: !!c.conditionField && c.value.trim() !== '',
      message: c.value.trim() !== ''
        ? `${c.conditionField} ${c.operator} ${c.value} is valid for this method`
        : 'The condition has no value to compare against',
    },
    {
      check: 'Action target valid',
      pass: flowNames.includes(c.matchedFlow) && flowNames.includes(c.elseFlow),
      message: flowNames.includes(c.matchedFlow) && flowNames.includes(c.elseFlow)
        ? `${c.thenAction} can be applied to ${c.thenTarget}`
        : 'Matched or Else flow is not one of the prep flows listed for this method',
    },
    {
      check: 'Rule conflict',
      pass: !duplicatePriority,
      message: duplicatePriority
        ? 'Two rules share the same priority — the winner would be arbitrary'
        : 'No overlapping higher-priority rule found',
    },
    {
      check: 'Default prep flow set',
      // Without a default, a sample matching no rule has nowhere to go.
      pass: c.prepFlows.some(f => f.isDefault),
      message: c.prepFlows.some(f => f.isDefault)
        ? `${c.prepFlows.find(f => f.isDefault)!.name} is the fallback when no rule matches`
        : 'No default prep flow — a sample matching no rule would not be routed',
    },
  ];
}

/** Preview of the id a new configuration WOULD get.
 *  Preview only: the real id must come from ISequentialIdGenerator so two people
 *  adding a method at once cannot both be handed ACT-008 (§14a Rule B). */
export function nextActIdPreview(configs: ActConfig[]): string {
  const highest = configs.reduce((max, c) => {
    const n = parseInt(c.catId.replace(/\D/g, ''), 10);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `ACT-${String(highest + 1).padStart(3, '0')}`;
}
